import { Anchor, Grid, Stack, Title } from '@mantine/core';
import { useEffect } from 'react';

import { Declaration, EnvironmentCountries, MethodsOfPayment } from '@e-origin/shared';

import { isHighValueDeclaration } from '@utils';
import { FieldRow, PolicyField, useFinalizeFieldRules } from '../policy';
import { ChargesImportSection } from './ChargesImportSection';

interface FinalizeCustomsTreatmentProps {
  declaration: Declaration;
  disabled: boolean;
}

const PAYMENT_METHOD = 'messageInfo.methodOfPayment';
const VAT_PAYMENT_METHOD = 'messageInfo.methodOfPaymentVAT';
const VALUATION_INDICATORS = 'valuationInformationTaxes.valuationAdjustment.valuationIndicators';
const DELIVERY_TERMS_INCOTERM = 'valuationInformationTaxes.deliveryTerms.incotermCode';

export const FinalizeCustomsTreatment: React.FC<FinalizeCustomsTreatmentProps> = ({ declaration, disabled }) => {
  // form, scope, codelists and the two helpers - no `form` prop, no useSettings here.
  // `section` matters for the delivery-terms trio only: it lives here for BE/NL export and in the
  // Transport tab otherwise.
  const { form, ctx, rule, fieldProps } = useFinalizeFieldRules({ section: 'customs' });

  const customerChanged = declaration.customer !== form.values.customer;

  // feat(ORI-1379): the disable+auto-clear pair below is a whole-form concern (depends on a sibling
  // field's live value, not on the variant), so it stays hand-computed - same as
  // ChargesImportSection's missingCurrencyProps pair. The error MESSAGE itself is a straight
  // cross-field validation rule and lives in the registry (nl.import.ts's `validate`, on both this
  // field and messageInfo.deferredPaymentVAT), reaching <PolicyField> through `form.errors` like any
  // other validated field - see useFormPolicy.ts's `validateInputOnBlur`.
  const isNlHighValue = ctx.country === EnvironmentCountries.NL && isHighValueDeclaration(ctx.group);
  const isVatDeferred = isNlHighValue && form.values.messageInfo.methodOfPaymentVAT === MethodsOfPayment.OTHER;

  useEffect(() => {
    if (isVatDeferred && form.values.messageInfo.deferredPaymentVAT) {
      form.setFieldValue('messageInfo.deferredPaymentVAT', '');
    }
  }, [isVatDeferred]);

  if (!declaration) {
    return null;
  }

  return (
    <>
      <Stack>
        <FieldRow mb="sm">
          <PolicyField {...fieldProps(PAYMENT_METHOD)} span={6} />
          <PolicyField {...fieldProps('messageInfo.deferredPayment')} span={6} disabledWhen={customerChanged} />
        </FieldRow>
        <FieldRow mb="sm">
          <PolicyField {...fieldProps(VAT_PAYMENT_METHOD)} span={6} eucdmPath={PAYMENT_METHOD} />
          <PolicyField {...fieldProps('messageInfo.deferredPaymentVAT')} span={6} disabledWhen={customerChanged || isVatDeferred} />
        </FieldRow>
        <FieldRow>
          <PolicyField {...fieldProps('valuationInformationTaxes.valuationMethod')} span={4} />
          <PolicyField {...fieldProps(VALUATION_INDICATORS)} span={4} ignoreReadOnly={true} />
          {rule(VALUATION_INDICATORS).present && (
            <Grid.Col span={1}>
              <Anchor href={'/help'}>Valuation indicators description</Anchor>
            </Grid.Col>
          )}
        </FieldRow>
        <FieldRow mb="sm">
          <PolicyField {...fieldProps(DELIVERY_TERMS_INCOTERM)} span={4} />
          <PolicyField {...fieldProps('valuationInformationTaxes.deliveryTerms.location')} span={4} />
          <PolicyField {...fieldProps('valuationInformationTaxes.deliveryTerms.country')} span={4} />
        </FieldRow>
      </Stack>

      <Stack>
        <Title order={4}>Total</Title>
        <FieldRow mb="sm">
          <PolicyField
            {...fieldProps('declarationGlobal.totalWeight')}
            span={2.4}
            value={declaration.globalInfo?.totalWeight || ''}
            eucdmPath={'goodsShipmentItems.goodsMeasure.grossMass'}
          />
          <PolicyField {...fieldProps('declarationGlobal.totalAmountInvoiced')} span={2.4} value={declaration.globalInfo?.totalAmountInvoiced || ''} />
          <PolicyField {...fieldProps('goodShipmentGlobal.invoiceCurrency')} span={2.4} value={declaration.globalInfo?.invoiceCurrency || ''} />
          <PolicyField {...fieldProps('declarationGlobal.totalPCS')} span={2.4} value={declaration.globalInfo?.totalPCS || ''} eucdm={false} />
          <PolicyField {...fieldProps('declarationGlobal.totalCNTS')} span={2.4} value={declaration.globalInfo?.totalCNTS || ''} eucdm={false} />
        </FieldRow>
        <FieldRow mb="sm">
          <PolicyField {...fieldProps('declarationGoods.packaging.typeOfPackages')} span={12} eucdmPath={'goodsShipmentItems.packaging.typeOfPackages'} />
        </FieldRow>
      </Stack>

      <ChargesImportSection declaration={declaration} />
    </>
  );
};
